/**
 * Training-completion modal shown after the agent acts on the
 * `/properties/create` page (MRG-281). Three variants per the Figma:
 *
 * - **submitted** (node 67:589) — agent submitted to investors
 * - **draft** (node 67:599) — agent saved a draft
 * - **exit** (node 82:624) — agent left the page without submitting
 *
 * The modal is rendered after `completeTraining` has been called; clicking
 * Close releases the agent into the now-real platform.
 */

import { Button } from '@/common/components/button'
import { Dialog } from '@/common/components/dialog'

import * as styles from './CompletionModal.css'

export type CompletionType = 'submitted' | 'draft' | 'exit'

const COPY: Record<CompletionType, { title: string; body: string }> = {
  submitted: {
    title: 'Congrats, you’ve submitted your first property!',
    body: 'You can visit the Offers page to keep track your offers. We’ll also notify you by email.',
  },
  draft: {
    title: 'You’re close to submitting your first property!',
    body: 'You can find your draft property here on the My Properties page and publish the property once you’re ready to start receiving offers.',
  },
  exit: {
    title: 'You’ve completed your training!',
    body: 'You can now submit new properties to receive offers.',
  },
}

export interface CompletionModalProps {
  open: boolean
  type: CompletionType
  onClose: () => void
}

export function CompletionModal({ open, type, onClose }: CompletionModalProps) {
  const { title, body } = COPY[type]
  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onClose() }}>
      <Dialog.Content width={463} showOverlay data-testid={`agent-training-completion-${type}`}>
        <div className={styles.card}>
          <Dialog.Title className={styles.title}>{title}</Dialog.Title>
          <Dialog.Description className={styles.body}>{body}</Dialog.Description>
          <div className={styles.buttonRow}>
            <Button variant="primary" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </Dialog.Content>
    </Dialog>
  )
}
